import { useNavigate } from 'react-router-dom';
import { ArrowRight, Home, Users, Mountain, Sparkles } from 'lucide-react';

const NAVY = '#0F172A';
const INDIGO = '#4F46E5';
const SLATE_2 = '#6B7280';
const BORDER = '#E2E8F0';
const SOFT_BG = '#F8FAFC';

/* Clinity gradient — matches highlighted words across the site */
const GRADIENT_STYLE = {
  background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 35%, #8B5CF6 70%, #A855F7 100%)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  color: 'transparent',
  WebkitTextFillColor: 'transparent',
};

function OptionCard({ testid, icon: Icon, title, body, ctaLabel, to, accent, accentBg, gradient }) {
  return (
    <div
      className="group relative rounded-[28px] p-8 md:p-10 bg-white transition-all duration-300 hover:-translate-y-1 flex flex-col"
      style={{
        border: `1px solid ${BORDER}`,
        boxShadow: '0 2px 8px rgba(15,23,42,0.05)',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 32px 60px -24px rgba(15,23,42,0.18)')}
      onMouseLeave={(e) => (e.currentTarget.style.boxShadow = '0 2px 8px rgba(15,23,42,0.05)')}
      data-testid={testid}
    >
      {/* Soft brand halo on hover */}
      <div
        aria-hidden="true"
        className="absolute -top-16 -right-16 w-56 h-56 rounded-full blur-3xl opacity-0 group-hover:opacity-40 transition-opacity duration-500 pointer-events-none"
        style={{ background: `radial-gradient(circle, ${accent}55 0%, transparent 70%)` }}
      />

      <div
        className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110"
        style={{ background: accentBg }}
      >
        <Icon className="w-6 h-6" style={{ color: accent }} strokeWidth={2.1} />
      </div>

      <h2
        className="text-[26px] sm:text-[30px] font-bold leading-[1.15]"
        style={{ fontFamily: 'Outfit, sans-serif', color: NAVY, letterSpacing: '-0.02em' }}
      >
        {title}
      </h2>
      <p
        className="mt-4 text-[15px] sm:text-[16px] leading-[1.65]"
        style={{ color: SLATE_2 }}
      >
        {body}
      </p>

      <div className="mt-8 pt-6" style={{ borderTop: `1px solid ${BORDER}`, marginTop: 'auto' }}>
        <a
          href={to}
          data-testid={`${testid}-cta`}
          className="group/cta inline-flex items-center justify-center gap-2.5 px-7 py-3.5 rounded-full font-semibold text-[14px] sm:text-[15px] text-white no-underline transition-all duration-300 hover:-translate-y-0.5"
          style={{
            background: gradient,
            boxShadow: `0 18px 36px -14px ${accent}80, inset 0 1px 0 rgba(255,255,255,0.18)`,
            letterSpacing: '-0.005em',
          }}
        >
          {ctaLabel}
          <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover/cta:translate-x-1" strokeWidth={2.2} />
        </a>
      </div>
    </div>
  );
}

export default function ImproveYourScore() {
  const navigate = useNavigate();

  return (
    <div
      className="min-h-screen"
      data-testid="improve-page"
      style={{ background: `linear-gradient(180deg, ${SOFT_BG} 0%, #FFFFFF 100%)`, color: NAVY, fontFamily: 'Manrope, sans-serif' }}
    >
      {/* NAV */}
      <nav className="sticky top-0 z-40 backdrop-blur-xl border-b" style={{ backgroundColor: 'rgba(255,255,255,0.85)', borderColor: BORDER }} data-testid="improve-nav">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <a href="/" className="text-lg font-bold no-underline" style={{ color: NAVY, letterSpacing: '-0.02em', fontFamily: 'Outfit, sans-serif' }}>Clinity</a>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all hover:-translate-y-0.5 bg-white"
            style={{ border: `1px solid ${BORDER}`, boxShadow: '0 2px 8px rgba(15,23,42,0.06)' }}
            data-testid="improve-nav-home"
          >
            <Home className="w-4 h-4" /> Home
          </button>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24">
        {/* HEADER */}
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] font-bold uppercase" style={{ background: '#EEF2FF', color: INDIGO, letterSpacing: '0.22em' }}>
            <Sparkles className="w-3.5 h-3.5" /> Improve Your Score
          </div>
          <h1
            className="mt-6 text-[38px] sm:text-[48px] lg:text-[58px] font-bold leading-[1.06]"
            style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.03em', color: NAVY }}
            data-testid="improve-headline"
          >
            Choose how you want to <span style={GRADIENT_STYLE}>improve</span> your wellbeing
          </h1>
          <p
            className="mt-6 text-[16px] sm:text-[18px] leading-[1.65]"
            style={{ color: SLATE_2 }}
            data-testid="improve-subheadline"
          >
            Two paths. One goal — a stronger, more complete you.
          </p>
        </div>

        {/* TWO OPTION CARDS */}
        <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          <OptionCard
            testid="improve-option-experts"
            icon={Users}
            title="Wellness Experts"
            body="Connect with wellness experts across 8 dimensions to improve your overall wellbeing."
            ctaLabel="Explore Wellness Experts"
            to="/experts"
            accent="#6366F1"
            accentBg="#E0E7FF"
            gradient="linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #A855F7 100%)"
          />
          <OptionCard
            testid="improve-option-spaces"
            icon={Mountain}
            title="Wellness Spaces & Retreats"
            body="Discover wellness retreats, spaces and vacations that can help you disconnect, recharge and improve your wellbeing."
            ctaLabel="Explore Wellness Spaces & Retreats"
            to="/spaces"
            accent="#059669"
            accentBg="#D1FAE5"
            gradient="linear-gradient(135deg, #10B981 0%, #059669 60%, #047857 100%)"
          />
        </div>
      </main>
    </div>
  );
}
